/**
 * EventLog - Unified event log panel that replaces scattered toasts
 * Displays game events in a compact, non-intrusive log window
 */

import { FACTIONS } from '@/game/factions/FactionData';
import type { FactionId } from '@/types';

export type EventType = 'territory' | 'battle' | 'capture' | 'defeat' | 'victory' | 'train' | 'build' | 'info';

export interface GameEvent {
  id: number;
  type: EventType;
  title: string;
  message: string;
  factionId?: FactionId;
  timestamp: number;
  count: number; // For merging similar events
}

export class EventLog {
  private container: HTMLElement;
  private logContent: HTMLElement;
  private events: GameEvent[] = [];
  private nextId: number = 1;
  private maxEvents: number = 50;
  private isExpanded: boolean = false;
  private unreadCount: number = 0;
  private mergeWindow: number = 3000; // Merge same events within 3 seconds

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'event-log';
    this.container.innerHTML = `
      <style>
        #event-log {
          position: fixed;
          top: 16px;
          right: 16px;
          z-index: 100;
          font: 12px/1.4 'Noto Serif SC', 'SimSun', serif;
          width: 280px;
          user-select: none;
        }

        .event-log-panel {
          background: linear-gradient(145deg, rgba(45,35,28,0.95) 0%, rgba(30,22,16,0.98) 100%);
          border: 2px solid #6b5020;
          border-radius: 6px;
          box-shadow:
            inset 0 1px 2px rgba(255,220,150,0.08),
            0 4px 16px rgba(0,0,0,0.5);
          overflow: hidden;
        }

        .event-log-header {
          background: linear-gradient(180deg, rgba(80,55,35,0.9) 0%, rgba(55,38,25,0.95) 100%);
          border-bottom: 1px solid #6b5020;
          padding: 8px 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          transition: background 0.2s;
        }

        .event-log-header:hover {
          background: linear-gradient(180deg, rgba(90,62,40,0.9) 0%, rgba(65,45,30,0.95) 100%);
        }

        .event-log-title {
          color: #c4a77d;
          font-weight: 600;
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .event-log-title-icon {
          font-size: 14px;
        }

        .event-log-badge {
          background: #c73e3a;
          color: #fff;
          font-size: 10px;
          padding: 1px 5px;
          border-radius: 8px;
          font-weight: 700;
          min-width: 16px;
          text-align: center;
          animation: badgePulse 0.3s ease-out;
        }

        @keyframes badgePulse {
          0% { transform: scale(1.3); }
          100% { transform: scale(1); }
        }

        .event-log-toggle {
          color: #9e8b6e;
          font-size: 14px;
          transition: transform 0.2s, color 0.2s;
        }

        .event-log-panel.expanded .event-log-toggle {
          transform: rotate(180deg);
        }

        .event-log-header:hover .event-log-toggle {
          color: #ffd35a;
        }

        .event-log-body {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.25s ease-out;
        }

        .event-log-panel.expanded .event-log-body {
          max-height: 300px;
        }

        .event-log-list {
          max-height: 260px;
          overflow-y: auto;
          padding: 4px 0;
        }

        .event-log-list::-webkit-scrollbar {
          width: 6px;
        }

        .event-log-list::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.2);
        }

        .event-log-list::-webkit-scrollbar-thumb {
          background: #6b5020;
          border-radius: 3px;
        }

        .event-log-list::-webkit-scrollbar-thumb:hover {
          background: #8b6914;
        }

        .event-item {
          padding: 6px 12px;
          display: flex;
          align-items: flex-start;
          gap: 8px;
          border-bottom: 1px solid rgba(107,80,32,0.2);
          transition: background 0.15s;
        }

        .event-item:last-child {
          border-bottom: none;
        }

        .event-item:hover {
          background: rgba(107,80,32,0.15);
        }

        .event-item.new {
          animation: eventHighlight 2s ease-out;
        }

        @keyframes eventHighlight {
          0% { background: rgba(255,200,100,0.2); }
          100% { background: transparent; }
        }

        .event-icon {
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 12px;
          border-radius: 3px;
          margin-top: 1px;
        }

        .event-icon.territory { background: linear-gradient(135deg, #4caf50, #2e7d32); }
        .event-icon.battle { background: linear-gradient(135deg, #c73e3a, #8b2020); }
        .event-icon.capture { background: linear-gradient(135deg, #ff9800, #e65100); }
        .event-icon.defeat { background: linear-gradient(135deg, #607d8b, #455a64); }
        .event-icon.victory { background: linear-gradient(135deg, #ffd700, #ff8f00); }
        .event-icon.train { background: linear-gradient(135deg, #5b9bd5, #3d7ab5); }
        .event-icon.build { background: linear-gradient(135deg, #8d6e63, #5d4037); }
        .event-icon.info { background: linear-gradient(135deg, #78909c, #546e7a); }

        .event-content {
          flex: 1;
          min-width: 0;
        }

        .event-text {
          color: #e8dcc8;
          font-size: 11px;
          line-height: 1.3;
          word-break: break-word;
        }

        .event-count {
          color: #ffd35a;
          font-weight: 700;
          margin-left: 4px;
        }

        .event-time {
          color: #7a6b52;
          font-size: 10px;
          margin-top: 2px;
        }

        .event-faction-dot {
          width: 8px;
          height: 8px;
          border-radius: 2px;
          margin-left: 4px;
          display: inline-block;
          vertical-align: middle;
        }

        /* Preview (collapsed state shows latest event) */
        .event-log-preview {
          padding: 6px 12px;
          color: #c4a77d;
          font-size: 11px;
          border-top: 1px solid rgba(107,80,32,0.3);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .event-log-panel.expanded .event-log-preview {
          display: none;
        }

        .event-log-preview .preview-icon {
          font-size: 10px;
          flex-shrink: 0;
        }

        .event-log-preview .preview-text {
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .event-log-clear {
          padding: 6px 12px;
          border-top: 1px solid rgba(107,80,32,0.3);
          text-align: center;
        }

        .event-log-clear-btn {
          background: none;
          border: 1px solid rgba(107,80,32,0.5);
          color: #9e8b6e;
          padding: 4px 12px;
          border-radius: 3px;
          font-size: 10px;
          cursor: pointer;
          transition: all 0.15s;
        }

        .event-log-clear-btn:hover {
          background: rgba(107,80,32,0.2);
          color: #c4a77d;
          border-color: #8b6914;
        }

        .event-log-empty {
          padding: 20px;
          text-align: center;
          color: #7a6b52;
          font-size: 11px;
        }

        /* Compact layout for small viewports (desktop 720p) */
        @media (max-height: 800px) {
          #event-log {
            top: 10px;
            right: 10px;
            width: 240px;
            font-size: 11px;
          }
          .event-log-header {
            padding: 5px 10px;
          }
          .event-log-title {
            font-size: 11px;
          }
          .event-log-preview {
            padding: 4px 10px;
            font-size: 10px;
          }
          .event-log-panel.expanded .event-log-body {
            max-height: 220px;
          }
          .event-log-list {
            max-height: 190px;
          }
          .event-item {
            padding: 4px 10px;
          }
          .event-icon {
            width: 16px;
            height: 16px;
            font-size: 10px;
          }
          .event-text {
            font-size: 10px;
          }
        }
      </style>

      <div class="event-log-panel">
        <div class="event-log-header">
          <span class="event-log-title">
            <span class="event-log-title-icon">📜</span>
            事件日志
            <span class="event-log-badge" style="display: none;">0</span>
          </span>
          <span class="event-log-toggle">▼</span>
        </div>
        <div class="event-log-preview">
          <span class="preview-icon">-</span>
          <span class="preview-text">暂无事件</span>
        </div>
        <div class="event-log-body">
          <div class="event-log-list"></div>
          <div class="event-log-clear">
            <button class="event-log-clear-btn">清空日志</button>
          </div>
        </div>
      </div>
    `;

    this.logContent = this.container.querySelector('.event-log-list')!;

    // Setup event listeners
    const header = this.container.querySelector('.event-log-header')!;
    header.addEventListener('click', () => this.toggle());

    const clearBtn = this.container.querySelector('.event-log-clear-btn')!;
    clearBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.clear();
    });

    document.body.appendChild(this.container);
  }

  private toggle(): void {
    this.isExpanded = !this.isExpanded;
    const panel = this.container.querySelector('.event-log-panel')!;
    panel.classList.toggle('expanded', this.isExpanded);

    if (this.isExpanded) {
      this.clearUnread();
    }
  }

  private clearUnread(): void {
    this.unreadCount = 0;
    const badge = this.container.querySelector('.event-log-badge') as HTMLElement;
    badge.style.display = 'none';
  }

  private updateUnread(): void {
    if (!this.isExpanded) {
      this.unreadCount++;
      const badge = this.container.querySelector('.event-log-badge') as HTMLElement;
      badge.textContent = this.unreadCount > 99 ? '99+' : this.unreadCount.toString();
      badge.style.display = 'inline-block';
    }
  }

  private updatePreview(): void {
    const preview = this.container.querySelector('.event-log-preview')!;
    const iconEl = preview.querySelector('.preview-icon')!;
    const textEl = preview.querySelector('.preview-text')!;

    if (this.events.length === 0) {
      iconEl.textContent = '-';
      textEl.textContent = '暂无事件';
    } else {
      const latest = this.events[0];
      iconEl.textContent = this.getIcon(latest.type);
      const countText = latest.count > 1 ? ` x${latest.count}` : '';
      textEl.textContent = `${latest.title}${countText}`;
    }
  }

  /**
   * Add a new event to the log
   */
  addEvent(
    title: string,
    message: string,
    type: EventType = 'info',
    factionId?: FactionId
  ): number {
    const now = Date.now();

    // Try to merge with recent similar event
    const recentSimilar = this.events.find(
      (e) =>
        e.type === type &&
        e.title === title &&
        now - e.timestamp < this.mergeWindow
    );

    if (recentSimilar) {
      recentSimilar.count++;
      recentSimilar.timestamp = now;
      recentSimilar.message = message; // Update to latest message
      this.renderEvents();
      this.updatePreview();
      return recentSimilar.id;
    }

    const event: GameEvent = {
      id: this.nextId++,
      type,
      title,
      message,
      factionId,
      timestamp: now,
      count: 1,
    };

    // Add to front of array (newest first)
    this.events.unshift(event);

    // Trim old events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(0, this.maxEvents);
    }

    this.renderEvents();
    this.updatePreview();
    this.updateUnread();

    return event.id;
  }

  private renderEvents(): void {
    if (this.events.length === 0) {
      this.logContent.innerHTML = '<div class="event-log-empty">暂无事件记录</div>';
      return;
    }

    this.logContent.innerHTML = this.events
      .map((event, index) => this.renderEvent(event, index === 0))
      .join('');
  }

  private renderEvent(event: GameEvent, isNew: boolean): string {
    const icon = this.getIcon(event.type);
    const timeStr = this.formatTime(event.timestamp);
    const countStr = event.count > 1 ? `<span class="event-count">x${event.count}</span>` : '';

    let factionDot = '';
    if (event.factionId) {
      const faction = FACTIONS[event.factionId];
      const color = '#' + faction.color.toString(16).padStart(6, '0');
      factionDot = `<span class="event-faction-dot" style="background: ${color}"></span>`;
    }

    return `
      <div class="event-item${isNew ? ' new' : ''}" data-id="${event.id}">
        <div class="event-icon ${event.type}">${icon}</div>
        <div class="event-content">
          <div class="event-text">${event.title}${countStr}${factionDot}</div>
          <div class="event-time">${timeStr}</div>
        </div>
      </div>
    `;
  }

  private getIcon(type: EventType): string {
    switch (type) {
      case 'territory':
        return '🏴';
      case 'battle':
        return '⚔';
      case 'capture':
        return '🏛';
      case 'defeat':
        return '💀';
      case 'victory':
        return '👑';
      case 'train':
        return '🗡';
      case 'build':
        return '🔨';
      case 'info':
      default:
        return 'ℹ';
    }
  }

  private formatTime(timestamp: number): string {
    const now = Date.now();
    const diff = Math.floor((now - timestamp) / 1000);

    if (diff < 5) return '刚刚';
    if (diff < 60) return `${diff}秒前`;
    if (diff < 3600) return `${Math.floor(diff / 60)}分钟前`;
    return `${Math.floor(diff / 3600)}小时前`;
  }

  // Helper methods for common events
  notifyTerritory(factionName: string): void {
    this.addEvent('领土扩张', `从${factionName}夺取领土`, 'territory');
  }

  notifyBattle(attackerFaction: FactionId, defenderFaction: FactionId): void {
    const attacker = FACTIONS[attackerFaction];
    const defender = FACTIONS[defenderFaction];
    this.addEvent('战斗爆发', `${attacker.name} 进攻 ${defender.name}`, 'battle');
  }

  notifyCapture(factionId: FactionId, buildingType: string): void {
    const faction = FACTIONS[factionId];
    const buildingNames: Record<string, string> = {
      barracks: '兵营',
      tower: '箭塔',
      mine: '矿场',
      farm: '农场',
      market: '市场',
      stable: '马厩',
      smithy: '铁匠铺',
      temple: '道观',
      capital: '王城',
    };
    this.addEvent(
      '建筑占领',
      `${faction.name} 占领了 ${buildingNames[buildingType] || buildingType}`,
      'capture',
      factionId
    );
  }

  notifyDefeat(factionId: FactionId): void {
    const faction = FACTIONS[factionId];
    this.addEvent('势力覆灭', `${faction.name} 已被消灭`, 'defeat', factionId);
  }

  notifyVictory(factionId: FactionId): void {
    const faction = FACTIONS[factionId];
    this.addEvent('天下归一', `${faction.name} 统一了天下!`, 'victory', factionId);
  }

  notifyUnitTrained(unitType: string): void {
    const unitNames: Record<string, string> = {
      sword: '剑士',
      archer: '弓兵',
      spear: '枪兵',
      cavalry: '骑兵',
      shield: '盾兵',
      taoist: '道士',
    };
    this.addEvent('训练完成', `${unitNames[unitType] || unitType} 已就绪`, 'train');
  }

  notifyBuildingComplete(buildingType: string): void {
    const buildingNames: Record<string, string> = {
      barracks: '兵营',
      tower: '箭塔',
      mine: '矿场',
      farm: '农场',
      market: '市场',
      stable: '马厩',
      smithy: '铁匠铺',
      temple: '道观',
      capital: '王城',
    };
    this.addEvent('建造完成', `${buildingNames[buildingType] || buildingType} 已建成`, 'build');
  }

  clear(): void {
    this.events = [];
    this.renderEvents();
    this.updatePreview();
    this.clearUnread();
  }

  dispose(): void {
    this.container.remove();
  }
}
